import React, { useState } from 'react';
import { AccountType } from './ChartOfAccounts';
import { AccountTypeBadge } from './AccountTypeBadge';
import { PlusIcon, PencilIcon, TrashIcon, SaveIcon } from 'lucide-react';
interface AccountTypeManagerProps {
  accountTypes: AccountType[];
  onSave: (accountType: AccountType) => void;
  onDelete: (accountTypeId: string) => void;
  locale: string;
}
export function AccountTypeManager({
  accountTypes,
  onSave,
  onDelete,
  locale
}: AccountTypeManagerProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editingType, setEditingType] = useState<AccountType | null>(null);
  const [formData, setFormData] = useState<AccountType>({
    id: '',
    name: '',
    description: '',
    color: 'blue'
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  // Available colors for account types
  const availableColors = [{
    name: 'Blue',
    value: 'blue'
  }, {
    name: 'Red',
    value: 'red'
  }, {
    name: 'Green',
    value: 'green'
  }, {
    name: 'Yellow',
    value: 'yellow'
  }, {
    name: 'Purple',
    value: 'purple'
  }, {
    name: 'Pink',
    value: 'pink'
  }, {
    name: 'Indigo',
    value: 'indigo'
  }, {
    name: 'Gray',
    value: 'gray'
  }, {
    name: 'Orange',
    value: 'orange'
  }, {
    name: 'Teal',
    value: 'teal'
  }];
  // Handle creating a new account type
  const handleAddNew = () => {
    setFormData({
      id: '',
      name: '',
      description: '',
      color: 'blue'
    });
    setEditingType(null);
    setIsEditing(true);
    setErrors({});
  };
  // Handle editing an existing account type
  const handleEdit = (accountType: AccountType) => {
    setFormData({
      ...accountType
    });
    setEditingType(accountType);
    setIsEditing(true);
    setErrors({});
  };
  // Handle form changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    // Clear error for this field if it exists
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };
  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Validate form
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    onSave(formData);
    setIsEditing(false);
    setEditingType(null);
  };
  // Handle delete confirmation
  const handleDeleteClick = (accountTypeId: string) => {
    if (window.confirm('Are you sure you want to delete this account type? This cannot be undone.')) {
      onDelete(accountTypeId);
    }
  };
  return <div className="space-y-6" data-id="element-6541">
      {/* Account Types List */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-6542">
        <div className="px-4 py-5 sm:px-6 flex justify-between items-center border-b border-gray-200" data-id="element-6543">
          <div data-id="element-6544">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-6545">
              Account Types
            </h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500" data-id="element-6546">
              Manage account types used in your chart of accounts
            </p>
          </div>
          <button type="button" onClick={handleAddNew} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-6547">
            <PlusIcon className="h-4 w-4 mr-2" data-id="element-6548" />
            Add Account Type
          </button>
        </div>
        <div className="overflow-hidden overflow-x-auto" data-id="element-6549">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-6550">
            <thead className="bg-gray-50" data-id="element-6551">
              <tr data-id="element-6552">
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-6553">
                  Name
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-6554">
                  Description
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-6555">
                  Color
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-6556">
                  Preview
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-6557">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-6558">
              {accountTypes.map(type => <tr key={type.id} data-id="element-6559">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-6560">
                    {type.name}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500" data-id="element-6561">
                    {type.description || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-6562">
                    {availableColors.find(c => c.value === type.color)?.name || type.color}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-6563">
                    <AccountTypeBadge type={type} size="md" data-id="element-6564" />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-6565">
                    <button onClick={() => handleEdit(type)} className="text-indigo-600 hover:text-indigo-900 mr-4" data-id="element-6566">
                      <PencilIcon className="h-4 w-4" data-id="element-6567" />
                    </button>
                    <button onClick={() => handleDeleteClick(type.id)} className="text-red-600 hover:text-red-900" data-id="element-6568">
                      <TrashIcon className="h-4 w-4" data-id="element-6569" />
                    </button>
                  </td>
                </tr>)}
              {accountTypes.length === 0 && <tr data-id="element-6570">
                  <td colSpan={5} className="px-6 py-4 text-center text-sm text-gray-500" data-id="element-6571">
                    No account types found. Click "Add Account Type" to create
                    one.
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>
      </div>
      {/* Edit Form */}
      {isEditing && <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-6572">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200" data-id="element-6573">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-6574">
              {editingType ? 'Edit Account Type' : 'Create New Account Type'}
            </h3>
          </div>
          <div className="px-4 py-5 sm:p-6" data-id="element-6575">
            <form onSubmit={handleSubmit} data-id="element-6576">
              <div className="space-y-6" data-id="element-6577">
                {/* Name */}
                <div data-id="element-6578">
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700" data-id="element-6579">
                    Name *
                  </label>
                  <div className="mt-1" data-id="element-6580">
                    <input type="text" name="name" id="name" value={formData.name} onChange={handleChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md ${errors.name ? 'border-red-300' : ''}`} data-id="element-6581" />
                    {errors.name && <p className="mt-1 text-sm text-red-600" data-id="element-6582">{errors.name}</p>}
                  </div>
                </div>
                {/* Description */}
                <div data-id="element-6583">
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700" data-id="element-6584">
                    Description
                  </label>
                  <div className="mt-1" data-id="element-6585">
                    <textarea name="description" id="description" rows={3} value={formData.description || ''} onChange={handleChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-6586" />
                  </div>
                </div>
                {/* Color */}
                <div data-id="element-6587">
                  <label htmlFor="color" className="block text-sm font-medium text-gray-700" data-id="element-6588">
                    Color
                  </label>
                  <div className="mt-1" data-id="element-6589">
                    <select id="color" name="color" value={formData.color} onChange={handleChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-6590">
                      {availableColors.map(color => <option key={color.value} value={color.value} data-id="element-6591">
                          {color.name}
                        </option>)}
                    </select>
                  </div>
                  {/* Preview */}
                  <div className="mt-3" data-id="element-6592">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-6593">
                      Preview
                    </label>
                    <AccountTypeBadge type={{
                  id: 'preview',
                  name: formData.name || 'Account Type',
                  color: formData.color
                }} size="md" data-id="element-6594" />
                  </div>
                </div>
                {/* Actions */}
                <div className="flex justify-end space-x-3 pt-4" data-id="element-6595">
                  <button type="button" onClick={() => {
                setIsEditing(false);
                setEditingType(null);
              }} className="px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-6596">
                    Cancel
                  </button>
                  <button type="submit" className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-6597">
                    <SaveIcon className="h-4 w-4 mr-2" data-id="element-6598" />
                    {editingType ? 'Save Changes' : 'Create Account Type'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>}
    </div>;
}